import { useRef, useState } from 'react';
import Modal from 'react-modal';
import { api, getApiUrl } from '../utils/apiConfig';
import './GroupCreativeUploadModal.css';

export function youtubeVideoId(value) {
  const text = value.trim();
  if (/^[\w-]{11}$/.test(text)) return text;
  try {
    const url = new URL(text);
    if (!['https:', 'http:'].includes(url.protocol)) return null;
    const host = url.hostname.toLowerCase().replace(/^www\./, '').replace(/^m\./, '');
    const id = host === 'youtu.be' ? url.pathname.slice(1) :
      ['youtube.com', 'youtube-nocookie.com'].includes(host)
        ? (url.searchParams.get('v') || url.pathname.match(/^\/(?:shorts|embed)\/([^/]+)$/)?.[1]) : null;
    return /^[\w-]{11}$/.test(id || '') ? id : null;
  } catch { return null; }
}

export default function GroupCreativeUploadModal({ campaigns, accountId, userId, onClose }) {
  const [step, setStep] = useState('action');
  const [source, setSource] = useState('video');
  const [video, setVideo] = useState('');
  const [file, setFile] = useState(null);
  const [landingPage, setLandingPage] = useState('');
  const [logo, setLogo] = useState('');
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const [results, setResults] = useState({});
  const [error, setError] = useState('');
  const submitted = Object.keys(results).length > 0;
  const needsLogo = campaigns.some(c => /pmax/.test(c.destination));
  const submit = async (event) => {
    event.preventDefault();
    if (lock.current) return;
    if (source === 'video' && !youtubeVideoId(video)) { setError('Enter a valid YouTube URL or 11-character video ID.'); return; }
    if (source === 'spreadsheet' && (!file || !/\.(xlsx|xls|xlsm)$/i.test(file.name))) { setError('Choose an Excel spreadsheet (.xlsx, .xls or .xlsm).'); return; }
    lock.current = true;
    setBusy(true);
    setError('');
    for (const campaign of campaigns) {
      if (results[campaign.id]?.status === 'queued') continue;
      setResults(previous => ({ ...previous, [campaign.id]: { status: 'sending' } }));
      const form = new FormData();
      form.append('groupCreativeUpload', 'true');
      if (source === 'video') form.append('youtubeVideo', youtubeVideoId(video));
      else {
        form.append('files', file);
        form.append('landingPageUrl', landingPage.trim());
        form.append('logoUrl', logo.trim());
      }
      try {
        const response = await api.post(getApiUrl(`/api/v2/campaigns/${campaign.id}/upload-creatives`), form,
          { params: { accountId, userId } });
        if (response.data?.success === false) throw new Error(response.data.error || 'Upload was rejected');
        setResults(previous => ({ ...previous, [campaign.id]: { status: 'queued' } }));
      } catch (err) {
        setResults(previous => ({ ...previous, [campaign.id]: { status: 'failed', error: err.response?.data?.error || err.message } }));
      }
    }
    lock.current = false;
    setBusy(false);
  };
  const complete = campaigns.every(c => results[c.id]?.status === 'queued');
  return (
    <Modal isOpen onRequestClose={() => !busy && onClose()} contentLabel={step === 'action' ? 'Group Edit' : 'Upload Creatives'}
      className="group-creative-modal" overlayClassName="group-creative-overlay" ariaHideApp={false}>
      <header><h2>{step === 'action' ? 'Group Edit' : 'Upload Creatives'}</h2>
        <button type="button" onClick={onClose} disabled={busy} aria-label="Close">×</button></header>
      <p>{campaigns.length} campaigns selected</p>
      <ul className="group-creative-campaigns" aria-live="polite">
        {campaigns.map(c => <li key={c.id}><span>{c.name}</span><span>
          {results[c.id]?.status === 'queued' ? 'Queued — see campaign activity' : results[c.id]?.status === 'sending' ? 'Submitting…' : results[c.id]?.error}
        </span></li>)}
      </ul>
      {step === 'action' ? <>
        <p>What would you like to do?</p>
        <button className="btn btn-primary" onClick={() => setStep('upload')}>Upload Creatives</button>
      </> : <form onSubmit={submit}>
        <fieldset disabled={busy || submitted}>
          <legend>Creative source</legend>
          <label><input type="radio" name="source" checked={source === 'video'} onChange={() => setSource('video')} /> YouTube video</label>
          <label><input type="radio" name="source" checked={source === 'spreadsheet'} onChange={() => setSource('spreadsheet')} /> Spreadsheet</label>
          {source === 'video' ? <>
            <label htmlFor="group-video">YouTube URL or video ID</label>
            <input id="group-video" value={video} onChange={e => setVideo(e.target.value)} placeholder="https://www.youtube.com/watch?v=…" required />
            <p>Adds the video only to enabled PMax asset groups and enabled Demand Gen ad groups in campaigns that have not ended. Paused groups and ended campaigns are skipped. Campaigns may remain paused while creatives are prepared. For an active Demand Gen group without a video ad, creates a paused video ad using an existing image or carousel ad’s copy, logo, landing page, and tracking. Review and enable new ads in Google Ads. Groups without reusable creative details need a complete spreadsheet. Search campaigns cannot receive a video.</p>
            <p>For a new video file, upload it to YouTube first, then paste its link here.</p>
          </> : <>
            <label htmlFor="group-creative-file">Creative spreadsheet</label>
            <input id="group-creative-file" type="file" accept=".xlsx,.xls,.xlsm" onChange={e => setFile(e.target.files[0] || null)} required />
            <p>The same workbook is processed separately for each selected campaign. Use an AdCopy sheet for headlines and descriptions, and a DemandGen or PMax sheet for final_url, business_name, long_headline, logo_image, marketing_image, square_marketing_image and youtube_video_url. This format supports video in both Demand Gen and PMax.</p>
            <p>Demand Gen applies the creative to every enabled ad group in campaigns that have not ended, and creates paused ads. Paused groups and ended campaigns are skipped. Campaigns may remain paused while creatives are prepared. Video ads require a logo, copy, business name and landing page; landscape and square marketing images are needed for image ads. Optionally add target_ad_group_id or target_ad_group_name to restrict Demand Gen to one active group. PMax creates a new paused asset group in each non-ended campaign using the supplied text, images and video. For adding only a video to existing active PMax asset groups, choose YouTube video.</p>
            <label htmlFor="group-landing">Landing page URL</label>
            <input id="group-landing" type="url" value={landingPage} onChange={e => setLandingPage(e.target.value)} required />
            <label htmlFor="group-logo">Logo URL{needsLogo ? ' (required for PMax)' : ' (optional)'}</label>
            <input id="group-logo" type="url" value={logo} onChange={e => setLogo(e.target.value)} required={needsLogo} />
          </>}
        </fieldset>
        {error && <p role="alert">{error}</p>}
        {submitted && <p role="status">Submission is separate from assignment. Check each campaign’s activity for the final result.</p>}
        <footer>
          {!submitted && <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => setStep('action')}>Back</button>}
          <button type="button" className="btn btn-secondary" disabled={busy} onClick={onClose}>{complete ? 'Done' : 'Close'}</button>
          {!complete && <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Submitting…' : submitted ? 'Retry failed submissions' : `Upload to ${campaigns.length} campaigns`}</button>}
        </footer>
      </form>}
    </Modal>
  );
}
